declare var window: any;

/**
 * Centralised Meta (Facebook) Pixel tracking.
 *
 * Goals (see Meta "improve your pixel" checklist):
 *  - Advanced Matching: send hashed customer parameters (em, ph, fn, ln, ct, st, zp, country, external_id).
 *  - Event deduplication: every event carries a stable `eventID`, mirrored into the dataLayer so a
 *    future server-side Conversions API (CAPI) stream can send the SAME event_id + event_name and Meta
 *    will merge the two. This makes the browser side "CAPI-ready" without a server yet.
 *  - Standardisation: emails lowercased/trimmed, phones in E.164 (digits + country code), SHA-256 hashing
 *    of all PII before it leaves the browser.
 *
 * NOTE: this is the BROWSER half only. IP address + user-agent and the actual Graph API call must be added
 * by a server (backend endpoint, server-side GTM, or a CAPI gateway) that consumes the `meta_pixel_event`
 * dataLayer pushes emitted here.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MetaUserDataInput {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;     // ISO-2, e.g. "NL"
  externalId?: string | null;  // customer external reference
}

/** Hashed advanced-matching object, ready for fbq('init', id, ...) and CAPI `user_data`. */
export interface MetaUserData {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  st?: string;
  zp?: string;
  country?: string;
  external_id?: string;
  fbp?: string; // raw, never hashed
  fbc?: string; // raw, never hashed
}

export interface MetaTrackOptions {
  /** Stable id shared with the server stream for deduplication (e.g. order reference). */
  eventId?: string;
  /** Customer data to (re)apply as Advanced Matching before firing. */
  userData?: MetaUserDataInput;
}

export interface MetaContentItem {
  id: string | number;
  quantity?: number;
  price?: number;
}

// ---------------------------------------------------------------------------
// Normalisation helpers (Meta's documented rules)
// ---------------------------------------------------------------------------

// Country ISO-2 -> international calling code, for E.164 phone normalisation.
const CALLING_CODES: Record<string, string> = { NL: '31', BE: '32', DE: '49', FR: '33', GB: '44' };
const DEFAULT_CALLING_CODE = '31'; // NL is the primary market

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/** Lowercase, trim, strip punctuation & internal whitespace (used for names, city, state, zip). */
function normalizeText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s]+/g, '')
    .replace(/[.,'"-]/g, '');
}

/** E.164-ish: digits only, with country code, no leading zeros. */
function normalizePhone(value: string, country?: string | null): string {
  let digits = String(value).replace(/[^0-9]/g, '');
  if (!digits) {
    return '';
  }
  // Drop international prefix "00".
  if (digits.startsWith('00')) {
    digits = digits.slice(2);
    return digits;
  }
  // National number starting with 0 -> replace with the country calling code.
  if (digits.startsWith('0')) {
    const code = (country && CALLING_CODES[country.toUpperCase()]) || DEFAULT_CALLING_CODE;
    digits = code + digits.slice(1);
  }
  return digits;
}

// ---------------------------------------------------------------------------
// SHA-256 (Web Crypto). Returns undefined if hashing is unavailable so raw PII is never sent.
// ---------------------------------------------------------------------------

async function sha256Hex(value: string): Promise<string | undefined> {
  try {
    const subtle = window.crypto && window.crypto.subtle;
    if (!subtle) {
      return undefined;
    }
    const data = new TextEncoder().encode(value);
    const buffer = await subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return undefined;
  }
}

async function hashIfPresent(value: string | null | undefined): Promise<string | undefined> {
  if (value == null || value === '') {
    return undefined;
  }
  return sha256Hex(value);
}

// ---------------------------------------------------------------------------
// Cookie / click-id readers
// ---------------------------------------------------------------------------

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Meta browser id cookie. */
export function getFbp(): string | undefined {
  return getCookie('_fbp');
}

/** Meta click id: prefer the _fbc cookie, fall back to the pre-formatted sessionStorage value. */
export function getFbc(): string | undefined {
  return getCookie('_fbc') || window.sessionStorage.getItem('fbclid') || undefined;
}

// ---------------------------------------------------------------------------
// Build hashed user_data
// ---------------------------------------------------------------------------

export async function buildMetaUserData(raw: MetaUserDataInput): Promise<MetaUserData> {
  const [em, ph, fn, ln, ct, st, zp, external_id] = await Promise.all([
    hashIfPresent(raw.email ? normalizeEmail(raw.email) : undefined),
    hashIfPresent(raw.phone ? normalizePhone(raw.phone, raw.country) : undefined),
    hashIfPresent(raw.firstName ? normalizeText(raw.firstName) : undefined),
    hashIfPresent(raw.lastName ? normalizeText(raw.lastName) : undefined),
    hashIfPresent(raw.city ? normalizeText(raw.city) : undefined),
    hashIfPresent(raw.state ? normalizeText(raw.state) : undefined),
    hashIfPresent(raw.zip ? normalizeText(raw.zip) : undefined),
    hashIfPresent(raw.externalId ? String(raw.externalId).trim().toLowerCase() : undefined),
  ]);

  const userData: MetaUserData = {};
  if (em) userData.em = em;
  if (ph) userData.ph = ph;
  if (fn) userData.fn = fn;
  if (ln) userData.ln = ln;
  if (ct) userData.ct = ct;
  if (st) userData.st = st;
  if (zp) userData.zp = zp;
  if (external_id) userData.external_id = external_id;
  if (raw.country) userData.country = raw.country.trim().toLowerCase();

  const fbp = getFbp();
  const fbc = getFbc();
  if (fbp) userData.fbp = fbp;
  if (fbc) userData.fbc = fbc;

  return userData;
}

/**
 * Apply Advanced Matching for subsequent pixel events. Re-calling fbq('init', id, data)
 * updates the matching parameters; Meta de-dupes the init itself.
 */
export async function setMetaUserData(raw: MetaUserDataInput): Promise<MetaUserData> {
  const userData = await buildMetaUserData(raw);
  const pixelId = window.env && window.env.fbPixelId;
  if (window.fbq && pixelId) {
    window.fbq('init', pixelId, userData);
  }
  return userData;
}

// ---------------------------------------------------------------------------
// Event id (dedup key shared with the server stream)
// ---------------------------------------------------------------------------

export function generateEventId(): string {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Content helpers
// ---------------------------------------------------------------------------

/** Build Meta `contents`/`content_ids` params from a list of items. */
export function buildMetaContents(items: MetaContentItem[]): Record<string, unknown> {
  const contents = items.map((item) => ({
    id: String(item.id),
    quantity: item.quantity ?? 1,
    item_price: item.price ?? 0,
  }));
  return {
    content_type: 'product',
    content_ids: contents.map((c) => c.id),
    contents,
    num_items: contents.reduce((sum, c) => sum + (c.quantity || 1), 0),
  };
}

/** Convert GA4-style items ({ item_id, price, quantity }) into Meta content items. */
export function ga4ItemsToMetaContents(items: Array<Record<string, unknown>>): Record<string, unknown> {
  return buildMetaContents(
    items.map((i) => ({
      id: (i['item_id'] as string | number) ?? '',
      quantity: Number(i['quantity']) || 1,
      price: Number(i['price']) || 0,
    }))
  );
}

// ---------------------------------------------------------------------------
// Core tracking
// ---------------------------------------------------------------------------

/**
 * Fire a Meta standard event on the browser pixel with a dedup `eventID`, optionally applying
 * Advanced Matching first, and mirror the event into the dataLayer for a future CAPI stream.
 * Returns the eventID used (so callers can reuse it server-side).
 */
export async function trackMetaEvent(
  eventName: string,
  customData: Record<string, unknown> = {},
  options: MetaTrackOptions = {}
): Promise<string> {
  const eventId = options.eventId || generateEventId();

  let userData: MetaUserData | undefined;
  if (options.userData) {
    userData = await setMetaUserData(options.userData);
  }

  if (window.fbq) {
    window.fbq('track', eventName, customData, { eventID: eventId });
  }

  // CAPI-ready bridge: a server-side GTM tag / backend can pick this up and send the matching
  // server event with the same event_id + event_name for deduplication.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'meta_pixel_event',
    meta: {
      event_name: eventName,
      event_id: eventId,
      action_source: 'website',
      event_source_url: window.location ? window.location.href : undefined,
      custom_data: customData,
      user_data: userData || null,
    },
  });

  return eventId;
}

// ---------------------------------------------------------------------------
// Convenience wrappers for the standard events we use
// ---------------------------------------------------------------------------

export interface MetaEcommerceParams {
  value?: number;
  currency?: string;
  items?: MetaContentItem[];
  eventId?: string;
  userData?: MetaUserDataInput;
}

function ecommerceCustomData(params: MetaEcommerceParams): Record<string, unknown> {
  const data: Record<string, unknown> = {
    currency: params.currency ?? 'EUR',
  };
  if (params.value != null) {
    data['value'] = params.value;
  }
  if (params.items && params.items.length) {
    Object.assign(data, buildMetaContents(params.items));
  }
  return data;
}

export function trackMetaPurchase(params: MetaEcommerceParams): Promise<string> {
  return trackMetaEvent('Purchase', ecommerceCustomData(params), {
    eventId: params.eventId,
    userData: params.userData,
  });
}

export function trackMetaInitiateCheckout(params: MetaEcommerceParams): Promise<string> {
  return trackMetaEvent('InitiateCheckout', ecommerceCustomData(params), {
    eventId: params.eventId,
    userData: params.userData,
  });
}

export function trackMetaAddToCart(params: MetaEcommerceParams): Promise<string> {
  return trackMetaEvent('AddToCart', ecommerceCustomData(params), {
    eventId: params.eventId,
    userData: params.userData,
  });
}

export function trackMetaViewContent(params: MetaEcommerceParams): Promise<string> {
  return trackMetaEvent('ViewContent', ecommerceCustomData(params), {
    eventId: params.eventId,
    userData: params.userData,
  });
}

export function trackMetaLead(options: MetaTrackOptions = {}): Promise<string> {
  return trackMetaEvent('Lead', {}, options);
}

export function trackMetaCompleteRegistration(options: MetaTrackOptions = {}): Promise<string> {
  return trackMetaEvent('CompleteRegistration', {}, options);
}

// ---------------------------------------------------------------------------
// Expose on window so legacy AngularJS modules can call these too.
// ---------------------------------------------------------------------------

window.trackMetaEvent = trackMetaEvent;
window.setMetaUserData = setMetaUserData;
window.trackMetaLead = trackMetaLead;
window.trackMetaCompleteRegistration = trackMetaCompleteRegistration;
